import React, { useEffect } from 'react';
import { Gauge, DollarSign, Cpu, TrendingUp } from 'lucide-react';
import { useResourcesStore } from '@/stores/agents/resources-store';

const StatCard: React.FC<{ label: string; value: string; subtitle?: string; icon: React.ReactNode; color?: string }> = ({
  label, value, subtitle, icon, color = 'text-primary',
}) => (
  <div className="border border-border rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={color}>{icon}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
  </div>
);

export const ResourcesTab: React.FC = () => {
  const { summary, tierBreakdown, isLoading, error, fetchSummary, fetchTierBreakdown } = useResourcesStore();

  useEffect(() => {
    fetchSummary();
    fetchTierBreakdown();
  }, []);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-muted-foreground">Loading resources...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-2">
          <p className="text-destructive">{error}</p>
          <button onClick={() => { fetchSummary(); fetchTierBreakdown(); }} className="text-sm text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!summary || !summary.hasBudget) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <div className="text-center space-y-4">
          <Gauge className="h-16 w-16 mx-auto text-muted-foreground/40" />
          <h3 className="text-lg font-medium text-muted-foreground">No Budget Configured</h3>
          <p className="text-sm text-muted-foreground/70 max-w-md">
            Configure a daily budget to enable resource governance for your agent communities.
          </p>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90">
            Configure Budget
          </button>
        </div>
      </div>
    );
  }

  const tierLabels = ['', 'Frontier', 'Capable', 'Fast/Cheap', 'Deterministic'];

  return (
    <div className="h-full overflow-y-auto p-6 space-y-6">
      {/* Budget overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Daily Budget"
          value={`$${summary.totalBudgetCostUsd?.toFixed(2) || '0.00'}`}
          subtitle={`${(summary.totalBudgetTokens / 1000000).toFixed(1)}M tokens`}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <StatCard
          label="Used"
          value={`$${summary.totalCostUsed?.toFixed(2) || '0.00'}`}
          subtitle={`${((summary.totalTokensUsed || 0) / 1000).toFixed(0)}K tokens`}
          icon={<TrendingUp className="h-5 w-5" />}
          color="text-amber-500"
        />
        <StatCard
          label="Remaining"
          value={`$${((summary.totalBudgetCostUsd || 0) - (summary.totalCostUsed || 0)).toFixed(2)}`}
          icon={<DollarSign className="h-5 w-5" />}
          color="text-emerald-500"
        />
        <StatCard
          label="Utilization"
          value={`${(summary.costUtilization || 0).toFixed(1)}%`}
          subtitle={`Token: ${(summary.tokenUtilization || 0).toFixed(1)}%`}
          icon={<Gauge className="h-5 w-5" />}
          color={summary.costUtilization > 80 ? 'text-red-500' : 'text-primary'}
        />
      </div>

      {/* Model tier breakdown */}
      {tierBreakdown.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Usage by Model Tier</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {tierBreakdown.map((tier: any) => (
              <div key={tier._id} className="border border-border rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Cpu className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Tier {tier._id}: {tierLabels[tier._id] || ''}</span>
                </div>
                <p className="text-lg font-bold">${tier.totalCost?.toFixed(3) || '0'}</p>
                <p className="text-xs text-muted-foreground">
                  {tier.requestCount} requests | {((tier.totalTokensInput + tier.totalTokensOutput) / 1000).toFixed(0)}K tokens
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Community breakdown */}
      {summary.communityBreakdown?.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Community Allocations</h3>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">Community</th>
                  <th className="text-right p-3 font-medium">Allocated</th>
                  <th className="text-right p-3 font-medium">Used</th>
                  <th className="text-right p-3 font-medium">Utilization</th>
                </tr>
              </thead>
              <tbody>
                {summary.communityBreakdown.map((c: any) => (
                  <tr key={c.communityId} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{c.communityId.slice(0, 8)}...</td>
                    <td className="p-3 text-right">${c.cost_allocated?.toFixed(2) || '0.00'}</td>
                    <td className="p-3 text-right">${c.cost_used?.toFixed(3) || '0.00'}</td>
                    <td className="p-3 text-right">
                      <span className={c.costUtilization > 80 ? 'text-red-500' : ''}>
                        {c.costUtilization?.toFixed(1) || '0'}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
